import React, { useState, useCallback, useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  CALENDAR_FEATURE_NAME,
  CALENDAR_FEATURE_NAME_LEGACY,
  isCalendarIntegrationFeature,
  isComingSoonFeature,
} from "../../../constants/featureAccess";
import {
  getPlanDisplayPrice,
  getPlanCalendarText,
  getPlanEventsText,
  CALENDAR_STAFF_REQUEST_HINT,
} from "../../../utils/planLimits";

function findFeature(plan, featureName) {
  if (!plan || !plan.plan_details) return false;
  const detail = plan.plan_details.find(
    (d) =>
      d.feature_name === featureName ||
      (isComingSoonFeature(featureName) && isComingSoonFeature(d.feature_name)) ||
      (isCalendarIntegrationFeature(featureName) &&
        isCalendarIntegrationFeature(d.feature_name)) ||
      (featureName === CALENDAR_FEATURE_NAME &&
        d.feature_name === CALENDAR_FEATURE_NAME_LEGACY)
  );
  return detail ? detail.status === "E" : false;
}

const CARD_HIGHLIGHTS = [
  { label: "Connected calendars", type: "calendar" },
  { label: "Events", type: "events" },
  { label: "Group Events", feature: "Group Events" },
  { label: "Collect Payments", feature: "Collect Payments (PayPal, Stripe)" },
  { label: "Remove MeetOcto Branding", feature: "Removable MeetOcto Branding" },
  { label: "Automated Reminders", feature: "Automated Notifications and Reminders" },
];

const PLAN_BLURBS = {
  free: "For trying MeetOcto and creating your first booking page.",
  standard: "For professionals and small teams that need shared scheduling.",
  teams: "For organizations that need unlimited team workflows.",
};

function planBlurb(plan) {
  const name = (plan?.plan_name || "").toLowerCase();
  if (name.includes("free")) return PLAN_BLURBS.free;
  if (name.includes("standard")) return PLAN_BLURBS.standard;
  if (name.includes("team")) return PLAN_BLURBS.teams;
  return "Scheduling features tailored to your workspace.";
}

const CheckIcon = () => (
  <span className="pdt-icon pdt-icon--yes" aria-hidden="true">
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M3 7.2l2.6 2.6L11 4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const MinusIcon = () => (
  <span className="pdt-icon pdt-icon--no" aria-hidden="true">
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3.5 7h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  </span>
);

function PlanDetailModal({ plans = [], userPlan = {}, planPeriodType: externalPeriod = "monthly", onBuyPlan }) {
  const [isOpen, setIsOpen] = useState(false);
  const [period, setPeriod] = useState(externalPeriod);
  const navigate = useNavigate();

  const toggle = useCallback(() => setIsOpen((p) => !p), []);

  useEffect(() => {
    if (isOpen) setPeriod(externalPeriod || "monthly");
  }, [isOpen, externalPeriod]);

  const isMonthly = period === "monthly";

  const filteredPlans = [...plans].sort(
    (a, b) => Number(a.monthly_price) - Number(b.monthly_price)
  );

  const bestIdx = Math.floor(filteredPlans.length / 2);

  const handleBuyNow = (plan) => {
    onBuyPlan?.(plan);
    toggle();
  };

  const getPrice = (plan) => {
    if (!plan) return "0";
    return getPlanDisplayPrice(plan, isMonthly ? "monthly" : "annual");
  };

  const isCurrent = (plan) => plan?.id === userPlan?.plan_id;

  return (
    <>
      <button type="button" className="btn btn-outline brand pdt-trigger-btn" onClick={toggle}>
        View Details
      </button>

      <Modal isOpen={isOpen} toggle={toggle} className="pdt-modal modal-xl" centered>
        <ModalBody className="p-0">
          <div className="pdt-wrapper">
            <button className="pdt-close" onClick={toggle} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 3l8 8M11 3L3 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>

            <div className="pdt-header">
              <p className="pdt-kicker">Subscription</p>
              <h2 className="pdt-title">Choose your plan</h2>
              <p className="pdt-subtitle">
                Clear pricing for growing teams. Switch billing anytime.
              </p>

              <div className="pdt-period-toggle" role="group" aria-label="Billing period">
                <button
                  type="button"
                  className={`pdt-period-btn${isMonthly ? " active" : ""}`}
                  onClick={() => setPeriod("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`pdt-period-btn${!isMonthly ? " active" : ""}`}
                  onClick={() => setPeriod("annual")}
                >
                  Annual
                  <span className="pdt-save-badge">Save 20%</span>
                </button>
              </div>
            </div>

            <div className="pdt-cards-row">
              {filteredPlans.length === 0 && (
                <div className="pdt-loading">Loading plans…</div>
              )}
              {filteredPlans.map((plan, idx) => {
                const highlighted = idx === bestIdx;
                const current = isCurrent(plan);
                const isFree = Number(plan.monthly_price) === 0;

                return (
                  <article
                    key={plan.id}
                    className={`pdt-card${highlighted ? " pdt-card--featured" : ""}${
                      current ? " pdt-card--current" : ""
                    }`}
                  >
                    <div className="pdt-card-top">
                      <div className="pdt-card-labels">
                        <h3 className="pdt-plan-name">{plan.plan_name}</h3>
                        {highlighted && <span className="pdt-badge">Recommended</span>}
                        {current && !highlighted && (
                          <span className="pdt-badge pdt-badge--current">Current</span>
                        )}
                      </div>
                      <p className="pdt-plan-blurb">{planBlurb(plan)}</p>

                      <div className="pdt-price-block">
                        {isFree ? (
                          <div className="pdt-price-row">
                            <span className="pdt-price">$0</span>
                          </div>
                        ) : (
                          <div className="pdt-price-row">
                            <span className="pdt-currency">$</span>
                            <span className="pdt-price">{getPrice(plan)}</span>
                            <span className="pdt-period-wrap">
                              <span className="pdt-period">/mo</span>
                              <span className="pdt-per-user">per user</span>
                            </span>
                          </div>
                        )}
                        {!isMonthly && !isFree && (
                          <p className="pdt-annual-note">
                            Billed annually at ${plan.annual_price}/user
                          </p>
                        )}
                        {isFree && <p className="pdt-annual-note">Free forever</p>}
                      </div>
                    </div>

                    <button
                      type="button"
                      className={`pdt-cta${highlighted ? " pdt-cta--featured" : ""}${
                        current ? " pdt-cta--current" : ""
                      }`}
                      onClick={() => handleBuyNow(plan)}
                      disabled={current}
                    >
                      {current ? "Current plan" : isFree ? "Continue with Free" : "Select plan"}
                    </button>

                    <ul className="pdt-card-features">
                      {CARD_HIGHLIGHTS.map((row, ri) => {
                        let enabled = true;
                        let text = row.label;
                        if (row.type === "calendar") {
                          text = getPlanCalendarText(plan);
                        } else if (row.type === "events") {
                          text = getPlanEventsText(plan);
                        } else {
                          enabled = findFeature(plan, row.feature);
                        }
                        return (
                          <li key={ri} className={!enabled ? "pdt-feat-disabled" : ""}>
                            {enabled ? <CheckIcon /> : <MinusIcon />}
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </article>
                );
              })}
            </div>

            <p className="pdt-calendar-hint">{CALENDAR_STAFF_REQUEST_HINT}</p>

            <div className="pdt-compare-bar">
              <button
                type="button"
                className="pdt-compare-btn"
                onClick={() => {
                  toggle();
                  navigate("/admin/plan-comparison");
                }}
              >
                Compare all features
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default PlanDetailModal;
